import type { TaskVerificationStatus, TaskVerificationStatusData } from '@/types';

import showdown from 'showdown';

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(relativeTime);

//* ---- String formatting ----------------------------------------------------------------

const defaultFormattingOptions = {
  fractionDigits: 2,
  notation: 'standard',
} as {
  fractionDigits?: number;
  notation: 'compact' | 'standard';
};

// ---- NUMBERS & currency
export function formatCurrency(currency: string, amount: number | string, options = defaultFormattingOptions) {
  if (amount === null || amount === undefined) return 'N/A';
  return Intl.NumberFormat('en-US', {
    notation: options.notation,
    style: 'currency',
    currency,
    maximumFractionDigits: options.fractionDigits,
  }).format(Number(amount));
}
export function formatNumber(number: number | string, options = defaultFormattingOptions) {
  if (number === null || number === undefined) return 'N/A';
  return Intl.NumberFormat('en-US', {
    notation: options.notation,
    maximumFractionDigits: options.fractionDigits,
  }).format(Number(number));
}

// ---- FILES

export function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const formattedSize = (bytes / Math.pow(1024, i)).toFixed(2);
  return `${formattedSize} ${sizes[i]}`;
}

// ---- Convert String to Uppercase.
export function toUppercase(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

// ---- PROFILE
export function extractInitials(fullName: string) {
  const names = fullName.trim().split(' ');
  let initials = '';

  for (let i = 0; i < names.length; i++) {
    if (names[i]) {
      const name = names[i];
      initials += name[0].toUpperCase();
    }
  }

  return initials;
}

// ---- Date

export function getDateFromUtc(utcDateTime: string): Date {
  return new Date(utcDateTime?.substring(0, 25));
}
export function getDateFormat(date: string): String {
  return dayjs(date).format('DD/MM/YYYY');
}

/**  ISO - 8601 (YYYY - MM - DDTHH: mm: ss + ZZZZ) */
export function getDateFromISO(isoDateTime: string): Date {
  return new Date(isoDateTime);
}

export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATE_FORMAT_SERVER = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'DD MMM YYYY HH:mm';
export const DATE_TIME_FORMAT_ORDINAL = 'Do MMM, YYYY; HH:mm A';
export const DATE_TIME_FORMAT_ORDINAL_EXTENDED = 'dddd, Do MMMM, YYYY. HH:mm A';
export const DATE_TIME_FORMAT_SERVER = 'YYYY-MM-DDTHH:mm:ssZZ';
export const TIME_FORMAT = 'HH:mm';

interface DateFormatOptions {
  ordinal?: boolean;
  relative?: boolean;
  format?: string;
}

export function formatDateTime(value: string | Date | Dayjs, options?: DateFormatOptions): string {
  if (options?.relative) return dayjs(value).fromNow();

  const resolveDateFormatOptions = function (options?: DateFormatOptions) {
    if (options?.format) return options.format;
    if (options?.ordinal) return DATE_TIME_FORMAT_ORDINAL;
    return DATE_TIME_FORMAT;
  };

  // Parse DD/MM/YYYY format if it's a string
  const date = typeof value === 'string' && value.includes('/') ? dayjs(value, 'DD/MM/YYYY') : dayjs(value);

  return date.local().format(resolveDateFormatOptions(options));
}

// ---- Shorthands

export function channelShorthand(channel: string) {
  switch (channel) {
    case 'FACEBOOK':
      return 'FB';
    case 'INSTAGRAM':
      return 'Insta';
    case 'TWITTER':
      return 'Tw';
    case 'TIKTOK':
      return 'Tiktok';
    case 'LINKEDIN':
      return 'LinkedIn';
  }
  return channel;
}

//* ---- Markdown to HTML -----------------------------------------------------------------

const markdownConverter = new showdown.Converter();
markdownConverter.setFlavor('github');

export function md2html(md: string) {
  if (!md) return '';
  return markdownConverter.makeHtml(md.toString());
}

//* ---- Validation -----------------------------------------------------------------------

// ---- EMAIL VALIDATION
export function isValidEmail(email: string) {
  // Regular expression to match valid email addresses
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the email against the regular expression
  return emailRegex.test(email);
}

// ---- USERNAME VALIDATION
export const isValidUsername = (username: string) => {
  const validChars = /^[a-zA-Z\u00C0-\u024F0-9\-_.'\s]*$/;
  return validChars.test(username);
};

const now = new Date();

// ---- AGE VALIDATION
const _eighteenYearsAgo = new Date();
_eighteenYearsAgo.setFullYear(now.getFullYear() - 18);
export const eighteenYearsAgo = _eighteenYearsAgo;
export const eighteenYearsAgoFormat = dayjs(eighteenYearsAgo).format('YYYY-MM-DD');

export function isValidAge(date: string) {
  return new Date(date) <= eighteenYearsAgo;
}

// ---- PASSWORD VALIDATION
export function passwordValidations(password: string, confirmationPassword?: string): { label: string; isValid: boolean }[] {
  const validations = [
    {
      label: '8 or more characters',
      isValid: /.{8,}/.test(password),
    },
    {
      label: 'include at least one number',
      isValid: /\d+/.test(password),
    },
    {
      label: 'include at least one uppercase letter',
      isValid: /[A-Z]+/.test(password),
    },
    {
      label: 'include at least one lowercase letter',
      isValid: /[a-z]+/.test(password),
    },
    {
      label: 'include at least 1 symbol (!@#&$%)',
      isValid: /[!@#&$%]+/.test(password),
    },
  ];

  if (confirmationPassword !== null && confirmationPassword !== undefined) {
    validations.push({
      label: 'matches confirmation password',
      isValid: password === confirmationPassword && password.length >= 8,
    });
  }

  return validations;
}

export function isValidPassword(password: string): boolean {
  return !passwordValidations(password).find(e => e.isValid === false);
}

export function toSlug(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-');
}

export function fromSlug(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function capitalizeFirstLetter(str: string) {
  if (typeof str !== 'string' || str.length === 0) {
    return str; // Handle empty strings or non-string inputs
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function lowercaseRemainingLetters(str: string) {
  if (typeof str !== 'string' || str.length === 0) {
    return str; // Handle empty strings or non-string inputs
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

//* ---- Other helpers --------------------------------------------------------------------

export function getTaskStatus({
  first_verification_status,
  second_verification_status,
  third_verification_status,
}: TaskVerificationStatusData) {
  let status: TaskVerificationStatus | 'PENDING' = third_verification_status;

  if (third_verification_status === 'NOT_VERIFIED') {
    status = second_verification_status;
  }
  if (second_verification_status === 'NOT_VERIFIED') {
    status = first_verification_status;
  }
  if (first_verification_status === 'APPROVED' && [second_verification_status, third_verification_status].includes('NOT_VERIFIED')) {
    status = 'PENDING';
  }
  if (first_verification_status === 'APPROVED' && third_verification_status === 'APPROVED') {
    status = 'APPROVED';
  }
  if ([first_verification_status, second_verification_status, third_verification_status].includes('REJECTED')) {
    status = 'REJECTED';
  }

  return status;
}

// ------------------------- Plain text to text with links -------------------------.
/* eslint-disable no-useless-escape */
const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
export function textToTextWithLinks(plainText: string) {
  return plainText.replace(urlRegex, function (url: string) {
    return '<a style="color: #3f5be7;" target="_blank" href="' + url + '">' + url + '</a>';
  });
}

/**
 * Converts a duration in minutes to a human-readable string
 * @param totalMinutes - The total number of minutes to convert
 * @returns Formatted duration string like "1hr 30 mins", "3 days 2hrs 22 mins"
 * @example
 * formatMinutesToReadable(90) // "1hr 30 mins"
 * formatMinutesToReadable(4382) // "3 days 1hr 2 mins"
 * formatMinutesToReadable(60) // "1hr"
 * formatMinutesToReadable(45) // "45 mins"
 */
export function formatMinutesToReadable(totalMinutes: number): string {
  if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes)) {
    return 'N/A';
  }

  if (totalMinutes < 1) {
    return '0 min';
  }

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = Math.floor(totalMinutes % 60);

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  if (hours > 0) {
    parts.push(`${hours}hr${hours === 1 ? '' : 's'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
  }

  return parts.join(' ') || '< 1 min';
}

/**
 * Converts a full name to "FirstName M. L." format
 * @param fullName - The full name to convert
 * @returns Formatted name with first name and last initial (and middle initial if present)
 * @example
 * formatNameWithInitial("John Doe") // "John D."
 * formatNameWithInitial("John Michael Doe") // "John M. D."
 * formatNameWithInitial("Mary Jane Watson") // "Mary J. W."
 */
export function formatNameWithInitial(fullName: string): string {
  if (!fullName || typeof fullName !== 'string') {
    return '';
  }

  const nameParts = fullName
    .trim()
    .split(/\s+/)
    .filter(part => part.length > 0);

  if (nameParts.length === 0) {
    return '';
  }

  if (nameParts.length === 1) {
    // Only first name provided
    return nameParts[0];
  }

  if (nameParts.length === 2) {
    // First and last name
    const [firstName, lastName] = nameParts;
    return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
  }

  // Three or more names (first, middle(s), last)
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  const middleNames = nameParts.slice(1, -1);

  // Get initials for all middle names
  const middleInitials = middleNames.map(name => `${name.charAt(0).toUpperCase()}.`).join(' ');

  return `${firstName} ${middleInitials} ${lastName.charAt(0).toUpperCase()}.`;
}
